import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { getCurrentTenant } from "../config/tenants.js";
import { getTenantColors } from "../styles/utils/colors.js";
import { storage } from "../utils/storage.js";
import "./Tabs.css";

const Tabs = forwardRef(({ children, onSelected, onLogOut }, ref) => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedItemText, setSelectedItemText] = useState("");
  const tabsRef = useRef();
  
  // 테넌트 정보 가져오기 (storage에서 먼저 확인, 없으면 기본값)
  const tenant = storage.get("tenant_info") || getCurrentTenant();
  
  // 테넌트별 색상 가져오기
  const tenantColors = getTenantColors();

  const menuItems = React.Children.toArray(children).filter(
    (child) => child.type === "ul"
  );

  const isInitialized = useRef(false);

  const handleTabClick = useCallback((index) => {
    if (menuItems.length > 0 && menuItems[0].props.children[index]) {
      setSelectedIndex(index);
      setSelectedItemText(menuItems[0].props.children[index].props.children);
      if (onSelected) {
        onSelected({ args: { item: index } });
      }
    }
  }, [menuItems, onSelected]);

  // 초기화는 컴포넌트 마운트 시 한 번만 실행
  useEffect(() => {
    if (!isInitialized.current && menuItems.length > 0 && menuItems[0].props.children[0]) {
      isInitialized.current = true;
      setSelectedIndex(0);
      setSelectedItemText(menuItems[0].props.children[0].props.children);
      if (onSelected) {
        onSelected({ args: { item: 0 } });
      }
    }
  }, [menuItems, onSelected]);

  useImperativeHandle(ref, () => ({
    getTitleAt: (index) => menuItems[0]?.props.children[index]?.props.children,
  }));

  const contentItems = React.Children.toArray(children).filter(
    (child) => child.type !== "ul"
  );

  return (
    <div 
      className="tab-root" 
      ref={tabsRef}
      style={{
        '--tabs-selected-bg': tenantColors.tabsSelectedBg,
        '--tabs-selected-border': tenantColors.tabsSelectedBorder,
        '--tabs-selected-text': tenantColors.tabsSelectedText,
        '--tabs-menu-text': tenantColors.tabsMenuText,
        '--tabs-menu-hover-bg': tenantColors.tabsMenuHoverBg,
        '--tabs-menu-hover-text': tenantColors.tabsMenuHoverText,
        '--tabs-title-accent': tenantColors.tabsTitleAccent,
      }}
    >
      <div className="tab-menu">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "auto",
            height: "100%",
            backgroundColor: tenantColors.tabsMenuBg,
            borderRadius: "14px 0px 0px 14px",
          }}
        >
          <div
            style={{
              padding: "12px 6px",
              fontSize: "15px",
              color: tenantColors.tabsCompanyNameText,
              fontWeight: "900",
              textAlign: "center",
              backgroundColor: tenantColors.tabsCompanyNameBg,
              borderRadius: "14px 0px 0px 0px",
              letterSpacing: "0px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "48px",
              boxSizing: "border-box",
            }}
          >
            {tenant.companyName}
          </div>
          {menuItems.length > 0 && (
            <>
              {menuItems[0].props.children.map((child, index) => (
                <div
                  key={index}
                  className={`tabs-menu-item ${selectedIndex === index ? "selected" : ""}`}
                  onClick={() => handleTabClick(index)}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <div>
                    <img
                      src={
                        selectedIndex === index
                          ? child.props['data-selectedimageurl']
                          : child.props['data-imageurl']
                      }
                      alt={`${child.props['data-imageurl']}`}
                      style={{
                        maxHeight: "28px",
                        textAlign: "center",
                        margin: "3px 0px 2px 0px",
                      }}
                    />
                  </div>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {child}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="tab-body">
        <div className="tab-header-container">
          <div className="tab-title">
            {selectedItemText}
          </div>
          <div className="tab-header-right">
            <div className="tab-user-info">
              <span className="tab-workplace">
                {window.sessionStorage.getItem("workplace_nm") ?? ""}
              </span>
              <span className="tab-separator">|</span>
              <span className="tab-username">
                {window.sessionStorage.getItem("user_name") ?? ""}
              </span>
            </div>
            <Button
              onClick={onLogOut}
              variant="outlined"
              sx={{
                height: theme.custom.spacing.buttonHeight,
                minWidth: theme.custom.spacing.buttonWidth,
                padding: '8px 16px',
                fontSize: theme.typography.fontSizeScale.sm,
                fontWeight: theme.typography.fontWeight.medium,
                color: theme.palette.logout.text,
                backgroundColor: theme.palette.logout.main,
                border: '1px solid',
                borderColor: theme.palette.logout.border,
                borderRadius: '4px',
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.logout.hover,
                  borderColor: theme.palette.logout.borderHover,
                  color: theme.palette.logout.textHover,
                },
                '&:active': {
                  backgroundColor: theme.palette.logout.active,
                  borderColor: theme.palette.logout.borderHover,
                },
              }}
            >
              로그아웃
            </Button>
          </div>
        </div>
        {contentItems.map((content, index) => {
          const isSelected = selectedIndex === index;
          return (
            <div
              className="tab-content"
              key={index}
              style={{
                display: isSelected ? "block" : "none",
                height: "100%",
                boxSizing: "border-box",
              }}
            >
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default Tabs;
